import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import type { UserListItem, UserStatus } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { roleLabelTh } from '@/lib/role-label';
import { rolesListQueryOptions, useCreateUser, useUpdateUser, userErrorMessage } from '@/lib/users';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set → edit mode. */
  editing?: UserListItem;
}

const STATUS_OPTIONS: { value: UserStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];

interface FormState {
  email: string;
  username: string;
  name: string;
  password: string;
  roleId: string;
  status: UserStatus;
  bio: string;
}

const emptyState: FormState = {
  email: '',
  username: '',
  name: '',
  password: '',
  roleId: '',
  status: 'active',
  bio: '',
};

export function UserEditDialog({ open, onOpenChange, editing }: Props) {
  const isEdit = Boolean(editing);
  const rolesQuery = useQuery(rolesListQueryOptions());
  const create = useCreateUser();
  const update = useUpdateUser();
  const [state, setState] = useState<FormState>(emptyState);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFieldError(null);
    if (editing) {
      setState({
        email: editing.email,
        username: editing.username,
        name: editing.name,
        password: '',
        roleId: editing.role.id,
        status: editing.status,
        bio: '',
      });
    } else {
      setState({
        ...emptyState,
        roleId: rolesQuery.data?.find((r) => r.slug === 'author')?.id ?? '',
      });
    }
  }, [open, editing, rolesQuery.data]);

  const error = create.error ?? update.error;
  const isPending = create.isPending || update.isPending;

  const validate = (): string | null => {
    if (!state.email.trim() || !state.email.includes('@')) return 'อีเมลไม่ถูกต้อง';
    if (state.username.trim().length < 3) return 'username อย่างน้อย 3 ตัว';
    if (!/^[a-z0-9._-]+$/i.test(state.username)) return 'username ใช้ได้แค่ a-z 0-9 . _ -';
    if (!state.name.trim()) return 'กรุณาใส่ชื่อ';
    if (!state.roleId) return 'กรุณาเลือก role';
    if (!isEdit && state.password.length < 8) return 'รหัสผ่านอย่างน้อย 8 ตัว';
    if (isEdit && state.password && state.password.length < 8) return 'รหัสผ่านอย่างน้อย 8 ตัว';
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setFieldError(v);
      return;
    }
    setFieldError(null);

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          input: {
            email: state.email,
            username: state.username,
            name: state.name,
            roleId: state.roleId,
            status: state.status,
            bio: state.bio || null,
            ...(state.password ? { password: state.password } : {}),
          },
        });
      } else {
        await create.mutateAsync({
          email: state.email,
          username: state.username,
          name: state.name,
          password: state.password,
          roleId: state.roleId,
          status: state.status,
          bio: state.bio || null,
        });
      }
      onOpenChange(false);
    } catch {
      // surfaced via error
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'แก้ไขผู้ใช้' : 'สร้างผู้ใช้ใหม่'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'แก้ไขข้อมูล / role / สถานะ — เปลี่ยนรหัสผ่านโดยใส่ใน "รหัสผ่านใหม่"'
              : 'สร้างบัญชีใหม่และกำหนด role + รหัสผ่านเริ่มต้น'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          {(error || fieldError) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{fieldError ?? userErrorMessage(error)}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="u-email" className="mb-1.5 block">
                อีเมล *
              </Label>
              <Input
                id="u-email"
                type="email"
                value={state.email}
                onChange={(e) => setState({ ...state, email: e.target.value })}
                placeholder="user@example.com"
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="u-username" className="mb-1.5 block">
                Username *
              </Label>
              <Input
                id="u-username"
                value={state.username}
                onChange={(e) => setState({ ...state, username: e.target.value })}
                placeholder="johndoe"
                autoComplete="off"
                className="font-mono"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="u-name" className="mb-1.5 block">
              ชื่อแสดง *
            </Label>
            <Input
              id="u-name"
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              placeholder="John Doe"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="u-role" className="mb-1.5 block">
                Role *
              </Label>
              <select
                id="u-role"
                value={state.roleId}
                onChange={(e) => setState({ ...state, roleId: e.target.value })}
                className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="">— เลือก role —</option>
                {(rolesQuery.data ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {roleLabelTh(r.slug)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="u-status" className="mb-1.5 block">
                สถานะ
              </Label>
              <select
                id="u-status"
                value={state.status}
                onChange={(e) => setState({ ...state, status: e.target.value as UserStatus })}
                className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="u-password" className="mb-1.5 block">
              {isEdit ? 'รหัสผ่านใหม่ (เว้นว่างถ้าไม่เปลี่ยน)' : 'รหัสผ่าน *'}
            </Label>
            <PasswordInput
              id="u-password"
              value={state.password}
              onChange={(e) => setState({ ...state, password: e.target.value })}
              placeholder={isEdit ? 'เว้นว่างไว้' : 'อย่างน้อย 8 ตัว'}
              autoComplete="new-password"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              ยกเลิก
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEdit
                  ? 'กำลังบันทึก...'
                  : 'กำลังสร้าง...'
                : isEdit
                  ? 'บันทึก'
                  : 'สร้างผู้ใช้'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
